import { inject, Injectable, signal } from '@angular/core';
import { type Observable, ReplaySubject, Subject } from 'rxjs';
import { ModalCompareService } from './modal-compare.service';
import { type IModalWindowController, type ModalConfig, type ModalInstance } from './modal.types';
import { ReloadTriggerService } from './reload-trigger.service';

@Injectable({
  providedIn: 'root',
})
export class CoreModalService {
  private reload = inject(ReloadTriggerService);
  private compareService = inject(ModalCompareService);

  modals = signal<ModalInstance[]>([]);

  dismissed$: Observable<ModalInstance>;

  private dismissed = new Subject<ModalInstance>();

  constructor() {
    this.dismissed$ = this.dismissed.asObservable();
  }

  // shorthand method for simple modal
  showMiniModal(config: ModalConfig, outlet = 'root', parentController?: IModalWindowController): ReplaySubject<any> {
    return this.showModal(
      {
        ...config,
        style: 'mini',
        triggerReload: false, // mini modals (like datepicker) should not trigger a reload. This could mess up the current modal
      },
      outlet,
      parentController,
    );
  }

  // shorthand method for simple modal
  showSimpleModal<T>(
    config: ModalConfig,
    outlet = 'root',
    parentController?: IModalWindowController,
  ): ReplaySubject<T> {
    return this.showModal<T>(
      {
        ...config,
        style: 'simple',
        triggerReload: config.triggerReload ?? true,
      },
      outlet,
      parentController,
    );
  }

  showModal<T>(config: ModalConfig, outlet = 'root', parentController?: IModalWindowController): ReplaySubject<T> {
    const resultSubject = new ReplaySubject<T>();

    // set style
    // set defaults
    if (!config.style) {
      config.style = 'advanced';
    }

    if (config.scrollable === undefined) {
      config.scrollable = true;
    }

    if (config.showCloseButton === undefined) {
      config.showCloseButton = true;
    }

    // show titlebar by default for advanced and simple styles
    if (config.showTitleBar === undefined && (config.style === 'advanced' || config.style === 'simple')) {
      config.showTitleBar = true;
    }

    // if triggerReload is not set, we want to default to a reload
    if (config.triggerReload === undefined) {
      config.triggerReload = true;
    }

    // add modal
    this.modals.update((modals: ModalInstance[]) => {
      return [
        ...modals,
        {
          config,
          outlet,
          component: config.component,
          environmentInjector: config.environmentInjector,
          injector: config.injector,
          result: resultSubject,
          parentController: parentController as IModalWindowController,
        },
      ];
    });

    this.compareService.notifyModals(this.modals());

    return resultSubject;
  }

  // destroy the given modal. The instance is required, so we never close a modal that happens to be underneath
  closeModal(modalResult: any, modal: ModalInstance) {
    if (!modal) {
      return;
    }

    // remove the instance
    this.modals.update((openModals) => {
      return openModals.filter((openModal) => openModal !== modal);
    });

    this.compareService.notifyModals(this.modals());

    // let webModalService know the modal is closed, to handle router params.
    // this happens before the result is resolved, because a subscriber to the result can navigate
    // (or open another modal), and the last navigation wins. The router parameter cleanup should never
    // be that last navigation, or it would undo where the subscriber wants to go
    this.dismissed.next(modal);

    // resolve with result
    if (modal.result || modal.config.alwaysResolve) {
      if (modalResult !== undefined || modal.config.alwaysResolve) {
        modal.result.next(modalResult);
      }
      modal.result.complete();

      if (modal.config.triggerReload) {
        // used to get fresh action-items on dashboard, portfolio-home & notifications
        this.reload.triggerReload();
      }
    }
  }
}
